import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Eye, EyeOff, Loader2, ArrowLeft, MailCheck, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { initAdmin, forgotPassword, resetPassword, selfUnban } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  // view: "login" | "forgot" | "reset" | "unban"
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetMethod, setResetMethod] = useState("email"); // "email" | "totp"
  const [unbanCode, setUnbanCode] = useState("");
  const [appSettings, setAppSettings] = useState({
    logo_base64: null,
    company_name: "Charleroi South Brussels Airport"
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    initAdmin().catch(() => {});
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        if (response.data) {
          setAppSettings({
            logo_base64: response.data.logo_base64 || null,
            company_name: response.data.company_name || "Charleroi South Brussels Airport"
          });
        }
      } catch {
        // public settings are optional
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/select-module");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "PASSWORD_EXPIRED") {
        toast.error("Your password has expired. Please reset it with a code sent to your email.");
        await requestCode(email, true);
      } else if (error.response?.status === 429) {
        toast.error("Too many failed attempts. Please try again in a few minutes.");
      } else {
        toast.error(detail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const requestCode = async (targetEmail, expired = false) => {
    setLoading(true);
    try {
      const res = await forgotPassword(targetEmail);
      setEmail(targetEmail);
      setResetMethod("email");
      setView("reset");
      toast.success(res.data?.message || "A 6-digit reset code has been sent to your email.");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 404) {
        toast.error(detail || "No account found with this email address.");
      } else if (status === 429) {
        toast.error(detail || "Too many reset requests. Please wait a few minutes.");
      } else {
        toast.error(detail || "Could not start password reset. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const startTotpReset = () => {
    if (!email) { toast.error("Please enter your email first"); return; }
    setResetMethod("totp");
    setCode("");
    setView("reset");
  };

  const handleUnbanSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    if (!unbanCode || unbanCode.length !== 6) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    setLoading(true);
    try {
      const res = await selfUnban(email, unbanCode);
      toast.success(res.data?.message || "If the details were valid, access has been restored.");
      setUnbanCode("");
      setView("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not process the request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    await requestCode(email, false);
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error(resetMethod === "totp" ? "Enter the 6-digit code from your authenticator app" : "Enter the 6-digit code from your email");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, code, newPassword, resetMethod);
      toast.success("Password reset! You can now sign in.");
      setPassword("");
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      setView("login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "bg-white/10 border-white/20 text-white placeholder:text-gray-400 h-12 focus:ring-[#d4af37] focus:border-[#d4af37]";

  return (
    <div className="login-bg min-h-screen flex items-center justify-center p-4">
      <div className="glass-card rounded-2xl p-8 w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          {appSettings.logo_base64 ? (
            <img src={appSettings.logo_base64} alt="Company Logo" className="h-20 mb-4 object-contain" />
          ) : (
            <div className="w-16 h-16 bg-[#d4af37] rounded-2xl flex items-center justify-center mb-4">
              <Plane size={32} className="text-[#0f172a]" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white text-center font-['Outfit']">
            {appSettings.company_name}
          </h1>
          <p className="text-gray-300 mt-2">Business Aviation Application</p>
        </div>

        {/* LOGIN VIEW */}
        {view === "login" && (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-200">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={inputCls}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-200">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputCls} pr-12`}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setView("forgot"); }}
                className="text-sm text-[#d4af37] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-bold text-base"
              data-testid="login-submit-btn"
            >
              {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Signing in...</>) : "Sign In"}
            </Button>
            <button
              type="button"
              onClick={() => setView("unban")}
              className="w-full flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-white"
              data-testid="blocked-unblock-link"
            >
              <Smartphone size={14} /> Blocked by mistake? Unblock with your authenticator
            </button>
          </form>
        )}

        {/* FORGOT (request code) VIEW */}
        {view === "forgot" && (
          <form onSubmit={handleForgotSubmit} className="space-y-5" data-testid="forgot-form">
            <p className="text-gray-300 text-sm">
              Enter your email and we'll send you a 6-digit code to reset your password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-gray-200">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={inputCls}
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-bold text-base"
              data-testid="forgot-submit-btn"
            >
              {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending...</>) : "Send reset code by email"}
            </Button>
            <div className="flex items-center gap-3 text-gray-500 text-xs">
              <div className="h-px flex-1 bg-white/15" /> OR <div className="h-px flex-1 bg-white/15" />
            </div>
            <button
              type="button"
              onClick={startTotpReset}
              className="w-full h-12 flex items-center justify-center gap-2 rounded-md border border-white/20 text-white hover:bg-white/10 text-sm font-medium"
              data-testid="forgot-use-authenticator-btn"
            >
              <ShieldCheck size={18} /> Use my authenticator app instead
            </button>
            <button
              type="button"
              onClick={() => setView("login")}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-300 hover:text-white"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft size={16} /> Back to sign in
            </button>
          </form>
        )}

        {/* RESET (enter code + new password) VIEW */}
        {view === "reset" && (
          <form onSubmit={handleResetSubmit} className="space-y-5" data-testid="reset-form">
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              {resetMethod === "totp"
                ? (<><ShieldCheck size={18} /> Enter the code from your authenticator app</>)
                : (<><MailCheck size={18} /> Check your email for the 6-digit code</>)}
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-email" className="text-gray-200">Email</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="reset-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-code" className="text-gray-200">{resetMethod === "totp" ? "Authenticator code" : "6-digit code"}</Label>
              <Input
                id="reset-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className={`${inputCls} tracking-[0.5em] text-center text-lg`}
                data-testid="reset-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-new" className="text-gray-200">New password</Label>
              <Input
                id="reset-new"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 chars, a letter and a number"
                className={inputCls}
                data-testid="reset-new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm" className="text-gray-200">Confirm new password</Label>
              <Input
                id="reset-confirm"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                className={inputCls}
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-bold text-base"
              data-testid="reset-submit-btn"
            >
              {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Resetting...</>) : "Reset password"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setView("login")}
                className="flex items-center gap-2 text-gray-300 hover:text-white"
                data-testid="reset-back-to-login-btn"
              >
                <ArrowLeft size={16} /> Back to sign in
              </button>
              {resetMethod === "email" && (
                <button
                  type="button"
                  onClick={() => requestCode(email, false)}
                  className="text-[#d4af37] hover:underline"
                  data-testid="resend-code-btn"
                >
                  Resend code
                </button>
              )}
            </div>
          </form>
        )}

        {/* SELF-UNBAN VIEW (authenticator only) */}
        {view === "unban" && (
          <form onSubmit={handleUnbanSubmit} className="space-y-5" data-testid="unban-form">
            <p className="text-gray-300 text-sm">
              If your access was blocked by mistake, enter your email and a current code from your authenticator app to restore access. (Requires 2FA to be enabled on your account.)
            </p>
            <div className="space-y-2">
              <Label htmlFor="unban-email" className="text-gray-200">Email</Label>
              <Input
                id="unban-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={inputCls}
                data-testid="unban-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unban-code" className="text-gray-200">Authenticator code</Label>
              <Input
                id="unban-code"
                inputMode="numeric"
                maxLength={6}
                value={unbanCode}
                onChange={(e) => setUnbanCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className={`${inputCls} tracking-[0.5em] text-center text-lg`}
                data-testid="unban-code-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-bold text-base"
              data-testid="unban-submit-btn"
            >
              {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Verifying...</>) : "Restore my access"}
            </Button>
            <button
              type="button"
              onClick={() => setView("login")}
              className="w-full flex items-center justify-center gap-2 text-sm text-gray-300 hover:text-white"
              data-testid="unban-back-to-login-btn"
            >
              <ArrowLeft size={16} /> Back to sign in
            </button>
          </form>
        )}

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-8">
          © 2026 {appSettings.company_name}
        </p>
      </div>
    </div>
  );
}
